// A send is checkpointed before the box is cleared, then again with its bytes
// before dispatch. On reopening, replaying that exact mutation reconciles an
// answer lost after acceptance. A half-read file is named, never fabricated.
import { useEffect, useRef, useState } from 'react'
import { ApiError, errorMessage } from '../api/client'
import { uuid4 } from '../api/uuid'
import { toast } from '../components/toasts'
import type { PendingPrompt } from '../store/local'
import { useStore } from '../store/context'
import { filesKey, wireFiles } from './attachments'

export function promptFiles(held: PendingPrompt): File[] {
  return (held.attachments ?? []).map((file, index) => new File(
    [Uint8Array.from(atob(file.data), char => char.charCodeAt(0))], file.name,
    { type: file.mime_type, lastModified: held.files[index]?.lastModified },
  ))
}

export function usePendingPrompt(session: string, callbacks: {
  take: (text: string, files: readonly File[]) => void
  restore: (text: string, files: readonly File[]) => void
}) {
  const store = useStore()
  const cache = store.promptCache()
  const actions = useRef(callbacks)
  actions.current = callbacks
  const alive = useRef(false)
  const active = useRef(false)
  const held = useRef<PendingPrompt | null>(null)
  const [pending, setPending] = useState<PendingPrompt | null>(null)
  const [loading, setLoading] = useState(!!cache)
  const [sending, setSending] = useState(false)
  const keep = (next: PendingPrompt | null) => {
    held.current = next
    if (alive.current) setPending(next)
  }
  const fail = async (err: unknown, record: PendingPrompt, files: readonly File[]) => {
    if (!alive.current || cache !== store.promptCache()) return
    // These are definite refusals, rather than a lost answer or a timed-out request.
    if (err instanceof ApiError && [400, 409, 422].includes(err.status)) {
      const refused = { ...record, refused: true }
      try { await store.keepPrompt(refused, cache); keep(refused) }
      catch { /* The original checkpoint remains usable with its original mutation. */ }
    }
    if (!alive.current) return
    actions.current.restore(record.text, files)
    toast(`Could not send the prompt: ${errorMessage(err)}`, 'error')
  }
  const dispatch = async (record: PendingPrompt) => {
    if (!record.attachments || !alive.current || cache !== store.promptCache()) return false
    await store.sendSessionOp({ mutation_id: record.mutationId, op: 'prompt_session', args: {
      session_id: session, prompt: record.text,
      ...(record.attachments.length ? { attachments: record.attachments } : {}),
      ...(record.priority ? { priority: record.priority } : {}),
    } })
    // An acknowledgement is the only reason to forget a dispatched send.
    await cache?.finishPrompt(session, record.mutationId)
    keep(null)
    return true
  }
  const retry = async (record = held.current, recovered = false) => {
    if (!record?.attachments || active.current || store.getState().offline) return
    active.current = true
    setSending(true)
    const files = promptFiles(record)
    actions.current.take(record.text, files)
    try {
      if (await dispatch(record) && recovered && alive.current) toast('The prompt was sent.')
    }
    catch (err) { await fail(err, record, files) }
    finally {
      active.current = false
      if (alive.current) setSending(false)
    }
  }

  useEffect(() => {
    alive.current = true
    let cancelled = false
    if (cache) {
      void cache.readPrompt(session).then(async record => {
        if (cancelled) return
        keep(record)
        setLoading(false)
        if (!record) return
        if (record.attachments && !record.refused && !store.getState().offline) await retry(record, true)
        else actions.current.restore(record.text, promptFiles(record))
      }).catch((err: unknown) => {
        if (!cancelled) toast(`Could not read the saved prompt: ${errorMessage(err)}`, 'error')
        // Do not overwrite an unread checkpoint.
      })
    }
    return () => { cancelled = true; alive.current = false }
    // A checkpoint is read once for this composer, not again on each keystroke.
  }, [cache, session, store])

  const send = async (text: string, files: readonly File[], priority: 'now' | 'later') => {
    if (loading || active.current || store.getState().offline) return
    const before = held.current
    if (before?.attachments) {
      const same = before.text === text && JSON.stringify(filesKey(promptFiles(before))) === JSON.stringify(filesKey(files))
      if (same) await retry(before)
      else if (!before.refused) { toast('Send the earlier prompt again before sending another.', 'error'); return }
      if (same) return
    }
    if (before && !before.attachments && before.files.some(file => !files.some(found => found.name === file.name && found.size === file.size))) {
      toast('Some files need to be attached again before sending.', 'error')
      return
    }
    active.current = true
    setSending(true)
    let record: PendingPrompt = { session, mutationId: before && !before.attachments ? before.mutationId : uuid4(), text, files: files.map(file => ({ name: file.name, size: file.size, type: file.type, lastModified: file.lastModified })), priority }
    try {
      // Even a reload during FileReader leaves the names of missing files.
      await store.keepPrompt(record, cache)
      keep(record)
      if (!alive.current) return
      actions.current.take(text, files)
      record = { ...record, attachments: await wireFiles(files) }
      if (!alive.current) return
      await store.keepPrompt(record, cache)
      keep(record)
      await dispatch(record)
    } catch (err) { await fail(err, record, files) }
    finally {
      active.current = false
      if (alive.current) setSending(false)
    }
  }
  return { pending, loading, sending, send, retry: () => retry() }
}
